import { Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import React from 'react';
import { EntriesPerPage } from '../../../Component/Model/Table/Table';
import { FeedbackTableMapper } from './FeedbackTableMapper';
import { toast } from 'react-toastify';
import { ConfirmationDialog } from '../../../Component/Common/Components/ConfirmationDialog';
import { DataTable } from '../../../Component/Common/Components/DataTable/DataTable';
import { MDBox } from '../../../Component/Common/Components/MDBox';
import { MDTypography } from '../../../Component/Common/Components/MDTypography';
import { useRecoilState, useRecoilValue } from "recoil";
import { VerificationCompanyAtom } from "../../../State/VerificationCompany.Atom";
import { defaultObject } from "../../../Helpers/typescriptHelper";
import {
  FeedbackEmailDtoIn,
  FeedbackSearchRequest,
  VerificationAgencyClient
} from "../../../Component/Model/Dto/VerificationAgency/VerificationAgencyClient";
import {
  VerificationAgencyClientService
} from "../../../Component/Api/VerificationAgency/VerificationAgencyClient/VerificationAgencyClientService";
import { ColumnSortOption } from "../../../Component/Model/Dto/ColumnSortOption";
import { FeedbackEmailDialog } from "./FeedbackEmailDialog";
import { VerificationAgencyContacts } from "../../../Component/Model/Dto/VerificationAgency/VerificationAgencyContacts";
import { useHistory } from "react-router-dom";
import { verificationAgency } from '../../../Component/Common/Constants/routes';
import { VerificationAgencyClientApplication } from '../../../Component/Model/Dto/VerificationAgency/VerificationAgencyClientApplication';
import { VerificationAgencyClientApplicationService } from '../../../Component/Api/VerificationAgency/VerificationAgencyClientApplication/VerificationAgencyClientApplicationService';

export const FeedbackPage = () => {

  const [company, setCompany] = useRecoilState(VerificationCompanyAtom)
  const [feedbackData, setFeedbackData] = useState<VerificationAgencyClient.FeedbackResponseDto[]>(defaultObject);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({ defaultValue: 10 });
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [filteredItemsCount, setFilteredItemsCount] = useState<number>(0);
  const [totalItemsCount, setTotalItemsCount] = useState<number>(0);
  const [searchValue, setSearchValue] = useState<string>('');
  const [showConfirmationDialog, setShowConfirmationDialog] = useState<boolean>(false);
  const confirmDeleteMessage = "Are you sure you want to delete this feedback entry?";
  const [selectedFeedbackId, setSelectedFeedbackId] = useState<number>(0);
  const [columnSortOptions, setColumnSortOptions] = useState<ColumnSortOption[]>([]);
  const [showSendConfirmationDialog, setShowSendConfirmationDialog] = useState<boolean>(false);
  const [isSending, setIsSending] = useState<boolean>(false);
  const history = useHistory();

  const verificationCompany = useRecoilValue(VerificationCompanyAtom);
  const [application, setApplication] = React.useState<VerificationAgencyClientApplication.VerificationAgencyClientApplicationResponse>();

  const fetchApplication = async () => {
    try {
      const response = await VerificationAgencyClientApplicationService.GetById(verificationCompany.applicationId);
      setApplication(response.data);
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    fetchApplication();
  }, [verificationCompany.applicationId])

  useEffect(() => {
    searchFeedback();
  }, [searchValue, entriesPerPage, pageNumber, columnSortOptions]);

  const searchRequest: FeedbackSearchRequest = {
    Draw: 2,
    PageNumber: pageNumber,
    PageSize: entriesPerPage.defaultValue!,
    ColumnSortOptions: columnSortOptions,
    SearchValue: searchValue,
    VaClientId: company.vaClientId,
    VaClientApplicationId: verificationCompany.applicationId,
  };


  const entriesPerPageChanged = (entriesPerPage: number) => {
    setEntriesPerPage({ defaultValue: entriesPerPage });
    setPageNumber(1);
  }

  const pageNumberChanged = (pageNumber: number) => {
    setPageNumber(pageNumber);
  }

  const handleCloseConfirmation = () => {
    setShowConfirmationDialog(false);
    setSelectedFeedbackId(0);
  }

  const showDeleteConfirmation = (id: number) => {
    setShowConfirmationDialog(true);
    setSelectedFeedbackId(id);
  }

  const showSendFeedbackConfirmation = () => {
    setShowSendConfirmationDialog(true);
  }

  const handleRouteToFeedback = (id: number) => {
    setCompany({ ...company, feedbackId: id });
    history.push(`/${verificationAgency.company}/${verificationAgency.applications}/${verificationAgency.feedback}/details`);
  }


  const handleSendFeedback = async (contacts: VerificationAgencyContacts.ContactList[]) => {
    try {
      setIsSending(true);
      setShowConfirmationDialog(false);
      var model: FeedbackEmailDtoIn = { ContactList: contacts, VaClientId: company.vaClientId , VaClientApplicationId: verificationCompany.applicationId};
      await VerificationAgencyClientService.SendFeedbackLink(model);
      setIsSending(false);

      toast.success("Successfully sent client feedback");
      searchFeedback();
    } catch (error) {
      console.log(error)
    } finally {
      setShowSendConfirmationDialog(false);
    }
  }

  const columnSortingChanged = (sortOption: ColumnSortOption) => {
    setColumnSortOptions([sortOption]);
    setPageNumber(1);
  }

  const handleDelete = async () => {
    try {
      await VerificationAgencyClientService.DeleteFeedbackEntry(selectedFeedbackId);
      toast.success("Successfully deleted feedback");
      searchFeedback();
    } catch (error) {
      console.log(error)
    } finally {
      handleCloseConfirmation();
    }
  }

  const searchValueChanged = (searchValue: string) => {
    setSearchValue(searchValue);
  }

  const searchFeedback = async () => {
    setIsLoading(true);
    try {
      const feedbackReponse = await VerificationAgencyClientService.FeedbackSearch(searchRequest);
      setFeedbackData(feedbackReponse.data.results);
      setFilteredItemsCount(feedbackReponse.data.filteredItemsCount)
      setTotalItemsCount(feedbackReponse.data.totalItemsCount)
    } catch (error) { }
    finally {
      setIsLoading(false);
    }
  }

  const bulkActions = [
    {
      label: "Send Feedback Form",
      action: showSendFeedbackConfirmation
    }
  ]

  const tableData = FeedbackTableMapper(feedbackData, showDeleteConfirmation, handleRouteToFeedback);

  return (
    <div className='center'>
      <MDBox style={{ marginBottom: "10px" }}>
        <MDTypography variant='h4' fontWeight='bold'>
          {application ? `Application - ${application.referenceNumber}` : null}
        </MDTypography>
      </MDBox>
      <MDTypography graphy color='primary' variant='h6' fontWeight='bold' sx={{ mb: 1 }} >
        Feedback
      </MDTypography>
      <Grid container>
        <Grid item xs={12}>
          <DataTable
            key={totalItemsCount}
            table={tableData}
            canSearch
            entriesPerPage={entriesPerPage}
            pageNumber={pageNumber}
            filteredItemsCount={filteredItemsCount}
            totalItemsCount={totalItemsCount}
            entriesPerPageChanged={entriesPerPageChanged}
            pageNumberChanged={pageNumberChanged}
            searchValueChanged={searchValueChanged}
            isLoading={isLoading}
            bulkActions={bulkActions}
            manualSorting={false}
            columnSortingChanged={columnSortingChanged}
          ></DataTable>

          <ConfirmationDialog
            showDialog={showConfirmationDialog}
            handleAccept={handleDelete}
            handleCancel={handleCloseConfirmation}
            heading={"Confirm Delete"}
            message={confirmDeleteMessage}
          />

            {showSendConfirmationDialog &&
              <FeedbackEmailDialog
                onCloseModal={() => setShowSendConfirmationDialog(false)}
                showModal={showSendConfirmationDialog}
                vaClientId={company.vaClientId ?? 0}
                isLoading={isSending}
                onSend={handleSendFeedback}
              />
            }
          </Grid>
        </Grid>
      </div>
  )
}